import React, { useEffect } from "react";
import { Button, Divider, Form, Input, Select, Space } from "antd";
import { Mapping } from "../../types/mapping";
import { SdkCategory } from "../../types/sdk";
import { Requirement } from "../../types/requirement";

const { Option } = Select;

export interface MappingFormProps {
  mapping?: Mapping;
  isLoading?: boolean;
  onFinish: (mapping: Mapping) => void;
}

export const MappingForm = (props: MappingFormProps) => {
  const [form] = Form.useForm();
  const [isAdTypesVisible, setAdTypesVisible] = React.useState<boolean>(false);

  const onReset = () => {
    form.resetFields();
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues["category"]) {
      setAdTypesVisible(changedValues["category"] === "ad_network");
    }
  };

  const onFinish = (values: any) => {
    const mapping: Mapping = { id: props.mapping?.id, ...values };
    form.resetFields();
    props.onFinish(mapping);
  };

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(props.mapping);
  }, [props.mapping, form]);

  useEffect(() => {
    setAdTypesVisible(props.mapping?.category === "ad_network");
  }, [props.mapping]);

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={onValuesChange}
      onFinish={onFinish}
    >
      <Form.Item
        label="Status"
        name="status"
        rules={[{ required: true, message: "Please input status" }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="category"
        label="Category"
        rules={[{ required: true, message: "Please select category" }]}
      >
        <Select style={{ width: 150 }}>
          <Option value={SdkCategory.AD_NETWORK}>Ad Network</Option>
          <Option value={SdkCategory.MEDIATION}>Mediation</Option>
          <Option value={SdkCategory.SERVICE}>Service</Option>
          <Option value={SdkCategory.OTHER}>Other</Option>
        </Select>
      </Form.Item>
      {isAdTypesVisible && (
        <Form.Item name="adTypes" label="Ad Types">
          <Select mode="multiple">
            <Option value="banner">Banner</Option>
            <Option value="interstitial">Interstitial</Option>
            <Option value="rewarded_video">Rewarded Video</Option>
            <Option value="mrec">MREC</Option>
            <Option value="native">Native</Option>
          </Select>
        </Form.Item>
      )}
      <Form.Item
        name="requirement"
        label="Requirement"
        rules={[{ required: true, message: "Please select requirement" }]}
      >
        <Select style={{ width: 150 }}>
          <Option value={Requirement.DEFAULT}>Default</Option>
          <Option value={Requirement.OPTIONAL}>Optional</Option>
          <Option value={Requirement.REQUIRED}>Required</Option>
          <Option value={Requirement.DEPRECATED}>Deprecated</Option>
        </Select>
      </Form.Item>
      <Form.Item
        label="Display Name"
        name="display"
        rules={[{ required: true, message: "Please input name" }]}
      >
        <Input />
      </Form.Item>
      <Divider orientation="left">Appodeal</Divider>
      <Form.Item name="appodealDependencyIos" label="Appodeal iOS Adapter">
        <Input />
      </Form.Item>
      <Form.Item
        name="appodealDependencyAndroid"
        label="Appodeal Android Adapter"
      >
        <Input />
      </Form.Item>
      <Divider orientation="left">BidMachine</Divider>
      <Form.Item name="bidmachineDependencyIos" label="BidMachine iOS Adapter">
        <Input />
      </Form.Item>
      <Form.Item
        name="bidmachineDependencyAndroid"
        label="BidMachine Android Adapter"
      >
        <Input />
      </Form.Item>
      <Divider orientation="left">Bidon</Divider>
      <Form.Item name="bidonDependencyIos" label="Bidon iOS Adapter">
        <Input />
      </Form.Item>
      <Form.Item name="bidonDependencyAndroid" label="Bidon Android Adapter">
        <Input />
      </Form.Item>
      <Divider orientation="left">AppLovin MAX</Divider>
      <Form.Item name="maxDependencyIos" label="MAX iOS Adapter">
        <Input placeholder="AppLovinMediationGoogleAdapter" />
      </Form.Item>
      <Form.Item name="maxDependencyAndroid" label="MAX Android Adapter">
        <Input placeholder="com.applovin.mediation:google-adapter" />
      </Form.Item>
      <Divider orientation="left">Unity Level Play (IronSource)</Divider>
      <Form.Item name="levelPlayDependencyIos" label="LevelPlay iOS Adapter">
        <Input />
      </Form.Item>
      <Form.Item
        name="levelPlayDependencyAndroid"
        label="LevelPlay Android Adapter"
      >
        <Input />
      </Form.Item>
      <Divider />
      <Form.Item name="ownDependencyIos" label="iOS Pod">
        <Input />
      </Form.Item>
      <Form.Item name="ownDependencyAndroid" label="Android Artifact">
        <Input />
      </Form.Item>
      <Divider />
      <Form.Item name="sourceIos" label="iOS Source">
        <Input />
      </Form.Item>
      <Form.Item name="sourceAndroid" label="Android Source">
        <Input />
      </Form.Item>
      <Divider />
      <Form.Item name="skadnetwork" label="SKAdNetwork Source">
        <Input />
      </Form.Item>
      <Form.Item name="aakSource" label="AdAttributionKit Source">
        <Input />
      </Form.Item>
      <Form.Item name="warning" label="Warning">
        <Input placeholder="Warning message (e.g. 'No compatible versions')" />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button loading={props.isLoading} type="primary" htmlType="submit">
            {props.mapping ? "Update" : "Create"}
          </Button>
          <Button htmlType="button" onClick={onReset}>
            Reset
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
